import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React from 'react';
import classNames from 'classnames';
import isHotKey from 'is-hotkey';
import SlateHotkeys from 'slate-hotkeys';
import { IS_MAC } from '../../helpers/BrowserHelpers';
import CommandTypes from '../../types/CommandTypes';
import { ClockIcon, QuoteIcon } from '../../components/Icons';
import makeTypingReplacementShortcut from '../utils/makeTypingReplacementShortcut';
import makeToolbarButton, { EXPANDED_INSERTION } from '../utils/makeToolbarButton';
import makeKeyboardCommandShortcut from '../utils/makeKeyboardCommandShortcut';
import onTab from './handlers/onTab';
import onEnter from './handlers/onEnter';
import onBackspace from './handlers/onBackspace';
import onDelete from './handlers/onDelete';
import onCompositionFail from './handlers/onCompositionFail';
import onInputEmoji from './handlers/onInputEmoji';
import EntityTypes from '../../types/EntityTypes';
import renderInlines from '../utils/renderInlines';
import isInputEmoji from './utils/isInputEmoji';
import changeDepth from './commands/changeDepth';
import removeCurrentMarks from './commands/removeCurrentMarks';
import resetBlocks from './commands/resetBlocks';
import loremIpsum from './loremIpsum';
import './style.css';
var isDeleteBackward = SlateHotkeys.isDeleteBackward,
    isDeleteForward = SlateHotkeys.isDeleteForward;
var schema = {
  inlines: _defineProperty({}, EntityTypes.EMOJI, {
    isVoid: true
  })
};
var NON_SELECTABLE_INLINES = [EntityTypes.EMOJI, EntityTypes.LINE_BREAK];
export default (function () {
  var isForward = null;
  return [onCompositionFail(), {
    commands: {
      changeDepth: changeDepth,
      resetBlocks: resetBlocks,
      removeCurrentMarks: removeCurrentMarks,
      indent: function indent(editor) {
        return editor.changeDepth();
      },
      unIndent: function unIndent(editor) {
        return editor.changeDepth(-1);
      }
    },
    onMouseDown: function onMouseDown(event, editor, next) {
      /** We want to handle triple click ourselves, triple clicking browsers
       *  is fairly standard, and most often selection a whole HTML element.
       *  Slate will resolve this by putting the selection.end at the offset
       *  of the next block. We want the whole block to be selected, not the
       *  whole block PLUS the beginning of the next one
       * */
      if (event.detail <= 2 || editor.value.selection.isUnset) return next();
      var _editor$value = editor.value,
          selection = _editor$value.selection,
          startBlock = _editor$value.startBlock,
          endBlock = _editor$value.endBlock;
      event.preventDefault();
      event.stopPropagation();
      return editor.select(selection.moveStartToStartOfNode(startBlock).moveEndToEndOfNode(selection.end.offset > 0 ? endBlock : startBlock));
    },
    onKeyDown: function onKeyDown(event, editor, next) {
      // Ignore save keyboard shortcut
      if (isHotKey('mod+s', event)) {
        event.preventDefault();
        return next();
      }

      if (editor.value.selection.isUnset) return next();

      if (isHotKey('tab', event) || isHotKey('shift+tab', event)) {
        return onTab(event, editor, next);
      }

      if (isHotKey('enter', event)) {
        return onEnter(event, editor, next);
      }

      if (isDeleteBackward(event)) {
        return onBackspace(event, editor, next);
      }

      if (isDeleteForward(event)) {
        return onDelete(event, editor, next);
      } else if (isHotKey(['mod?+alt?+shift?+up', 'mod?+alt?+shift?+left'], event)) {
        isForward = false;
      } else if (isHotKey(['mod?+alt?+shift?+down', 'mod?+alt?+shift?+right'], event)) {
        isForward = true;
      }

      return next();
    },
    onBeforeInput: function onBeforeInput(event, editor, next) {
      if (isInputEmoji(event)) {
        return onInputEmoji(event, editor, next);
      }

      return next();
    },
    onChange: function onChange(editor, next) {
      var isVoid = editor.value.focusInline && NON_SELECTABLE_INLINES.includes(editor.value.focusInline.type);

      if (isVoid) {
        var selection = editor.value.selection;
        var isDirectionForward = selection.isCollapsed ? isForward : selection.isForward;
        var direction = isDirectionForward ? 'End' : 'Start';
        var anchor = selection.isCollapsed ? '' : direction;
        var nextPosition = isDirectionForward ? 'StartOfNext' : 'EndOfPrevious'; // collapsed forward -> moveToStartOfNextText
        // collapsed barckward -> moveToEndOfPreviousText
        // selected forward -> moveEndToStartOfNextText
        // selected barckward -> moveStartToEndOfPreviousText

        editor["move".concat(anchor, "To").concat(nextPosition, "Text")]();
      }

      return next();
    }
  }, makeToolbarButton(CommandTypes.TODAY, {
    icon: ClockIcon,
    title: "Today's date",
    visibility: EXPANDED_INSERTION,
    onApply: function onApply(editor) {
      return editor.insertText(new Date().toISOString().split('T')[0] + ' ');
    }
  }), makeToolbarButton(CommandTypes.LOREM_IPSUM, {
    icon: QuoteIcon,
    title: 'Lorem Ipsum',
    visibility: EXPANDED_INSERTION,
    canApply: function canApply() {
      return process.env.NODE_ENV === 'development';
    },
    onApply: function onApply(editor) {
      return editor.insertText(loremIpsum);
    }
  }), makeTypingReplacementShortcut(/(--)$/, '—'), makeTypingReplacementShortcut(/(-->)$/, '→')].concat(_toConsumableArray(IS_MAC ? [makeKeyboardCommandShortcut('ctrl+a', {
    command: 'moveToStartOfBlock'
  }), makeKeyboardCommandShortcut('ctrl+e', {
    command: 'moveToEndOfBlock'
  }), makeKeyboardCommandShortcut('ctrl+f', {
    command: 'moveForward'
  }), makeKeyboardCommandShortcut('ctrl+b', {
    command: 'moveBackward'
  })] : []), [_objectSpread({}, renderInlines(_defineProperty({}, EntityTypes.EMOJI, function (props, editor, next) {
    return React.createElement("span", Object.assign({
      className: classNames('note-editor-emoji', {
        'is-selected': props.isSelected
      })
    }, props.attributes), props.node.text);
  })), {
    schema: schema
  })]);
});